// verpassteAnrufe.tsx — Abschnitt „Verpasster Anruf" der Arbeitsansicht
// (User 01.10.2026): „Anrufe nur diese Woche und nur verpasste, die nicht
// angenommen wurden; Team und persönlich berücksichtigen."
//
// Die Kontakte kommen wie „Neue Mail" aus der gewöhnlichen Kontaktliste mit
// dem Filter `Arbeit: "verpasst"` — Suche, Spaltenfilter, Bereich und
// Sichtbarkeit gelten damit genauso. Die Regel selbst (offen verpasst, ab
// Montag, persönlich oder eigene Rufgruppe) steht im Backend
// (`apps/api/src/contactArbeitsansicht.ts`).
//
// Darunter der Hinweis auf verpasste Anrufe OHNE Kontakt — die gehören in die
// Telefonie, nicht in die Kontaktliste.

import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { apiFetch } from "../../../utils/apiFetch";
import { mapContactRow } from "../../../utils/contactRowMapper";
import { ANRUFE_GEAENDERT, telephonyService } from "../../../services/telephonyService";
import { useTelephonyOptional } from "../../../context/TelephonyContext";
import type { ContactDetailsFormValues } from "../../../types/Contact";
import { hinweisen } from "../bestaetigen";
import type { DataTableAbschnitt } from "./DataTable";
import type { ArbeitsZusatz } from "./kontaktKarten";
import { anrufZeitLabel, nachAnrufZeit, wochenbeginn } from "./arbeitsansicht";
import "./verpassteAnrufe.css";

export type VerpassteAnrufeAbfrage = {
  aktiv: boolean;
  search: string;
  /** Die übrigen Filter der Liste als JSON (wie für „Neue Mail"). */
  filtersJson: string;
  scope: string;
  includeTokens: string;
};

export type VerpassteAnrufe = {
  zeilen: ContactDetailsFormValues[];
  /** Offene verpasste Anrufe dieser Woche ohne Kontakt. */
  ohneKontakt: number;
  /** Kontakte, deren Erledigen gerade läuft. */
  laufend: ReadonlySet<number>;
  zurueckrufen: ((nummer: string, name?: string) => void) | null;
  erledigen: (kontaktId: number, callIds: number[]) => Promise<void>;
};

/** Lädt den Abschnitt und kennt die beiden Knöpfe der Zeile. */
export function useVerpassteAnrufe(a: VerpassteAnrufeAbfrage): VerpassteAnrufe {
  const telefon = useTelephonyOptional();
  const [zeilen, setZeilen] = useState<ContactDetailsFormValues[]>([]);
  const [ohneKontakt, setOhneKontakt] = useState(0);
  const [laufend, setLaufend] = useState<ReadonlySet<number>>(new Set());
  // Hochzählen = neu laden (nach „Erledigt").
  const [stand, setStand] = useState(0);

  useEffect(() => {
    if (!a.aktiv) {
      setZeilen([]);
      return;
    }
    const ctrl = new AbortController();
    apiFetch<any[]>("contact", {
      signal: ctrl.signal,
      params: {
        limit: 100,
        offset: 0,
        search: a.search,
        filters: JSON.stringify({ ...JSON.parse(a.filtersJson || "{}"), Arbeit: "verpasst" }),
        scope: a.scope,
        include: a.includeTokens,
        sort: "Name",
        direction: "asc",
      },
    })
      .then(({ data }) =>
        setZeilen(nachAnrufZeit((Array.isArray(data) ? data : []).map(mapContactRow).filter((r) => r.verpassterAnruf))),
      )
      .catch(() => {});
    return () => ctrl.abort();
  }, [a.aktiv, a.search, a.filtersJson, a.scope, a.includeTokens, stand]);

  // Ohne Kontakt: unabhängig von Suche und Filtern der Kontaktliste.
  // „Persönlich" der Telefonie = eigene Anrufe und die der eigenen Rufgruppen;
  // ohne Kontakt gibt es dort keinen FO/BO-Anteil.
  useEffect(() => {
    if (!a.aktiv) {
      setOhneKontakt(0);
      return;
    }
    const ctrl = new AbortController();
    telephonyService
      .listCalls(
        { missedOnly: true, ohneKontakt: true, scope: "own", from: wochenbeginn(new Date()).toISOString(), limit: 1 },
        { signal: ctrl.signal },
      )
      .then((r) => setOhneKontakt(typeof r.total === "number" ? r.total : 0))
      .catch(() => {});
    return () => ctrl.abort();
  }, [a.aktiv, stand]);

  const erledigen = useCallback(async (kontaktId: number, callIds: number[]) => {
    setLaufend((alt) => new Set(alt).add(kontaktId));
    try {
      const ergebnisse = await Promise.allSettled(callIds.map((id) => telephonyService.resolveCall(id)));
      const fehler = ergebnisse.filter((e) => e.status === "rejected").length;
      if (fehler) {
        await hinweisen({
          titel: "Nicht alles erledigt",
          text:
            fehler === callIds.length
              ? "Der Anruf ließ sich nicht als erledigt markieren. Bitte später noch einmal versuchen oder in der Telefonie erledigen."
              : `${fehler} von ${callIds.length} Anrufen ließen sich nicht als erledigt markieren.`,
        });
      }
    } finally {
      setLaufend((alt) => {
        const neu = new Set(alt);
        neu.delete(kontaktId);
        return neu;
      });
      setStand((n) => n + 1);
      window.dispatchEvent(new Event(ANRUFE_GEAENDERT));
    }
  }, []);

  const callNumber = telefon?.callNumber;
  return useMemo(
    () => ({
      zeilen,
      ohneKontakt,
      laufend,
      zurueckrufen: callNumber ? (nummer: string, name?: string) => callNumber(nummer, name) : null,
      erledigen,
    }),
    [zeilen, ohneKontakt, laufend, callNumber, erledigen],
  );
}

/** Der Abschnitt für die Tabelle bzw. die Handy-Karten; `null`, wenn es nichts zu zeigen gibt. */
export function verpasstAbschnitt<R extends ContactDetailsFormValues & { arbeitsZusatz?: ArbeitsZusatz }>(o: {
  anrufe: VerpassteAnrufe;
  offen: boolean;
  kopf: React.ReactNode;
  jetzt: Date;
}): DataTableAbschnitt<R> | null {
  const { zeilen, ohneKontakt, laufend, zurueckrufen, erledigen } = o.anrufe;
  if (!zeilen.length && !ohneKontakt) return null;
  const mitZusatz = (r: ContactDetailsFormValues): R => {
    const v = r.verpassterAnruf!;
    const nummer = v.nummer?.trim() || null;
    return {
      ...(r as R),
      arbeitsZusatz: {
        art: "anruf",
        zeit: anrufZeitLabel(v.zeit, o.jetzt),
        nummer,
        anzahl: v.anzahl,
        team: v.team,
        onZurueckrufen: nummer && zurueckrufen ? () => zurueckrufen(nummer, r.displayName || undefined) : undefined,
        onErledigt: () => void erledigen(r.kontaktId, v.callIds.length ? v.callIds : [v.callId]),
        laeuft: laufend.has(r.kontaktId),
      },
    };
  };
  const teile: DataTableAbschnitt<R>["teile"] = [
    { key: "anruf", zeilen: o.offen ? zeilen.map(mitZusatz) : [] },
  ];
  if (o.offen && ohneKontakt > 0) {
    teile.push({
      key: "ohne-kontakt",
      titel: (
        <Link to="/anrufe?tab=verpasst" className="kt-anruf-hinweis" data-testid="verpasst-ohne-kontakt">
          +{ohneKontakt} {ohneKontakt === 1 ? "verpasster Anruf" : "verpasste Anrufe"} ohne Kontakt → Telefonie
        </Link>
      ),
      zeilen: [],
    });
  }
  return { key: "anruf", className: "sec-anruf", kopf: o.kopf, teile };
}
